import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Bot, MessageSquare, AlertTriangle, Sparkles, Check, Trash2, Loader2 } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  getAssistantOverview,
  getAssistantTopQuestions,
  getAssistantDailyUsage,
  listRecentAssistantLogs,
  promoteAssistantLogToKb,
  markAssistantLogReviewed,
  listKbEntries,
  upsertKbEntry,
  deleteKbEntry,
} from "@/lib/api/ai-assistant-admin.functions";

export const Route = createFileRoute("/_authenticated/portal/admin/ai-assistant")({
  component: AdminAssistantPage,
});

function AdminAssistantPage() {
  const overviewFn = useServerFn(getAssistantOverview);
  const topFn = useServerFn(getAssistantTopQuestions);

  const overview = useQuery({
    queryKey: ["assistant-overview"],
    queryFn: () => overviewFn(),
    refetchInterval: 60_000,
  });
  const top = useQuery({
    queryKey: ["assistant-top"],
    queryFn: () => topFn(),
  });

  return (
    <div className="space-y-6 p-4 md:p-6">
      <header className="flex items-start gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-md bg-navy text-navy-foreground">
          <Bot className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display text-2xl text-navy">AI Legal Assistant</h1>
          <p className="text-sm text-muted-foreground">
            Track usage, review real client questions, and promote vetted
            answers into the assistant's knowledge base so it keeps improving.
          </p>
        </div>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Chats today" value={overview.data?.total_today} />
        <StatCard label="Last 7 days" value={overview.data?.total_7d} />
        <StatCard label="Last 30 days" value={overview.data?.total_30d} />
        <StatCard
          label="Escalations (30d)"
          value={overview.data?.escalated_30d}
          hint={
            overview.data && overview.data.total_30d > 0
              ? `${((overview.data.escalated_30d / overview.data.total_30d) * 100).toFixed(1)}% rate`
              : undefined
          }
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-4 lg:col-span-2">
          <h2 className="mb-3 font-display text-lg text-navy">
            Daily chats · last 30 days
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={overview.data?.daily ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="total" stroke="#0b1e3a" strokeWidth={2} dot={false} />
                <Line
                  type="monotone"
                  dataKey="escalated"
                  stroke="#c8a24b"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <h2 className="mb-3 font-display text-lg text-navy">Languages (30d)</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={overview.data?.languages ?? []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-25} height={60} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#0b1e3a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <DailyUsageChart />


      <Tabs defaultValue="review">
        <TabsList>
          <TabsTrigger value="review">
            <MessageSquare className="mr-2 h-4 w-4" /> Review conversations
          </TabsTrigger>
          <TabsTrigger value="top">
            <AlertTriangle className="mr-2 h-4 w-4" /> Top questions
          </TabsTrigger>
          <TabsTrigger value="kb">
            <Sparkles className="mr-2 h-4 w-4" /> Knowledge base
          </TabsTrigger>
        </TabsList>

        <TabsContent value="review" className="mt-4">
          <RecentLogs />
        </TabsContent>

        <TabsContent value="top" className="mt-4">
          <div className="rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">Question</th>
                  <th className="px-4 py-2 text-right">Times asked (30d)</th>
                </tr>
              </thead>
              <tbody>
                {(top.data ?? []).map((r, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-4 py-2 text-foreground/80">{r.question}</td>
                    <td className="px-4 py-2 text-right font-medium text-navy">{r.count}</td>
                  </tr>
                ))}
                {(top.data ?? []).length === 0 && (
                  <tr>
                    <td colSpan={2} className="px-4 py-8 text-center text-muted-foreground">
                      No questions logged yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="kb" className="mt-4">
          <KbManager />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | undefined;
  hint?: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-3xl text-navy">
        {value === undefined ? "—" : value.toLocaleString()}
      </p>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function isoDate(d: Date) {
  return d.toISOString().slice(0, 10);
}

function DailyUsageChart() {
  const usageFn = useServerFn(getAssistantDailyUsage);
  const today = new Date();
  const defaultFrom = new Date(today.getTime() - 13 * 86400_000);
  const [from, setFrom] = useState(isoDate(defaultFrom));
  const [to, setTo] = useState(isoDate(today));

  const usage = useQuery({
    queryKey: ["assistant-daily-usage", from, to],
    queryFn: () => usageFn({ data: { from, to } }),
  });

  const setPreset = (days: number) => {
    const t = new Date();
    setTo(isoDate(t));
    setFrom(isoDate(new Date(t.getTime() - (days - 1) * 86400_000)));
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg text-navy">Daily usage</h2>
          <p className="text-xs text-muted-foreground">
            Assistant chats per day for the selected range.
            {usage.data
              ? ` ${usage.data.total.toLocaleString()} total across ${usage.data.days} day${usage.data.days === 1 ? "" : "s"}${usage.data.peak.day ? ` · peak ${usage.data.peak.total} on ${usage.data.peak.day}` : ""}.`
              : null}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setPreset(7)}>7d</Button>
          <Button size="sm" variant="outline" onClick={() => setPreset(14)}>14d</Button>
          <Button size="sm" variant="outline" onClick={() => setPreset(30)}>30d</Button>
          <Button size="sm" variant="outline" onClick={() => setPreset(90)}>90d</Button>
          <Input
            type="date"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
            className="h-8 w-[150px]"
          />
          <span className="text-xs text-muted-foreground">to</span>
          <Input
            type="date"
            value={to}
            min={from}
            max={isoDate(new Date())}
            onChange={(e) => setTo(e.target.value)}
            className="h-8 w-[150px]"
          />
        </div>
      </div>
      <div className="h-64">
        {usage.isLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={usage.data?.daily ?? []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="total" fill="#0b1e3a" name="Chats" />
              <Bar dataKey="escalated" fill="#c8a24b" name="Escalated" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function RecentLogs() {
  const [onlyUnreviewed, setOnlyUnreviewed] = useState(true);
  const listFn = useServerFn(listRecentAssistantLogs);
  const promoteFn = useServerFn(promoteAssistantLogToKb);
  const reviewFn = useServerFn(markAssistantLogReviewed);
  const qc = useQueryClient();

  const logs = useQuery({
    queryKey: ["assistant-logs", onlyUnreviewed],
    queryFn: () => listFn({ data: { onlyUnreviewed, limit: 100 } }),
  });

  const promote = useMutation({
    mutationFn: (v: { logId: string; question: string; answer: string }) =>
      promoteFn({ data: v }),
    onSuccess: () => {
      toast.success("Added to the assistant's knowledge base");
      qc.invalidateQueries({ queryKey: ["assistant-logs"] });
      qc.invalidateQueries({ queryKey: ["assistant-kb"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to promote"),
  });

  const markReviewed = useMutation({
    mutationFn: (v: { logId: string; reviewed: boolean }) => reviewFn({ data: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assistant-logs"] }),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant={onlyUnreviewed ? "default" : "outline"}
          onClick={() => setOnlyUnreviewed(true)}
        >
          Needs review
        </Button>
        <Button
          size="sm"
          variant={!onlyUnreviewed ? "default" : "outline"}
          onClick={() => setOnlyUnreviewed(false)}
        >
          All recent
        </Button>
      </div>
      {logs.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (logs.data ?? []).length === 0 ? (
        <p className="rounded-xl border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground">
          No conversations to show.
        </p>
      ) : (
        <ul className="space-y-3">
          {(logs.data ?? []).map((l: any) => (
            <li key={l.id} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span>{new Date(l.created_at).toLocaleString()}</span>
                <span className="flex items-center gap-2">
                  {l.language ? <span className="rounded bg-muted px-2 py-0.5">{l.language}</span> : null}
                  {l.escalated ? (
                    <span className="rounded bg-gold/20 px-2 py-0.5 text-navy">Escalated</span>
                  ) : null}
                  {l.low_confidence ? (
                    <span className="rounded bg-red-100 px-2 py-0.5 text-red-700">Low conf.</span>
                  ) : null}
                  {l.promoted_to_kb ? (
                    <span className="rounded bg-green-100 px-2 py-0.5 text-green-700">In KB</span>
                  ) : null}
                </span>
              </div>
              <div className="mt-2 space-y-2 text-sm">
                <p>
                  <span className="font-semibold text-navy">Q: </span>
                  {l.question}
                </p>
                <p className="whitespace-pre-wrap text-foreground/80">
                  <span className="font-semibold text-navy">A: </span>
                  {l.answer}
                </p>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {!l.promoted_to_kb && (
                  <Button
                    size="sm"
                    onClick={() =>
                      promote.mutate({
                        logId: l.id,
                        question: l.question,
                        answer: l.answer,
                      })
                    }
                    disabled={promote.isPending}
                  >
                    <Sparkles className="mr-1 h-3.5 w-3.5" /> Add to knowledge base
                  </Button>
                )}
                {!l.reviewed && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => markReviewed.mutate({ logId: l.id, reviewed: true })}
                  >
                    <Check className="mr-1 h-3.5 w-3.5" /> Mark reviewed
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function KbManager() {
  const listFn = useServerFn(listKbEntries);
  const upsertFn = useServerFn(upsertKbEntry);
  const delFn = useServerFn(deleteKbEntry);
  const qc = useQueryClient();
  const entries = useQuery({
    queryKey: ["assistant-kb"],
    queryFn: () => listFn(),
  });
  const [q, setQ] = useState("");
  const [a, setA] = useState("");

  const upsert = useMutation({
    mutationFn: (v: any) => upsertFn({ data: v }),
    onSuccess: () => {
      toast.success("Saved");
      setQ("");
      setA("");
      qc.invalidateQueries({ queryKey: ["assistant-kb"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });
  const del = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assistant-kb"] }),
  });

  return (
    <div className="space-y-4">
      <form
        className="rounded-xl border border-border bg-card p-4 space-y-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!q.trim() || !a.trim()) {
            toast.error("Question and answer required");
            return;
          }
          upsert.mutate({ question: q, answer: a });
        }}
      >
        <h3 className="font-display text-navy">Add a vetted answer</h3>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Typical client question…"
        />
        <Textarea
          value={a}
          onChange={(e) => setA(e.target.value)}
          placeholder="The correct, compliant answer the assistant should give."
          rows={4}
        />
        <Button type="submit" disabled={upsert.isPending}>
          Save entry
        </Button>
      </form>

      <ul className="space-y-2">
        {(entries.data ?? []).map((e: any) => (
          <li key={e.id} className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>Updated {new Date(e.updated_at).toLocaleDateString()}</span>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={e.active}
                    onChange={(ev) =>
                      upsert.mutate({
                        id: e.id,
                        question: e.question,
                        answer: e.answer,
                        active: ev.target.checked,
                      })
                    }
                  />
                  active
                </label>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => del.mutate(e.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <p className="mt-2 text-sm">
              <span className="font-semibold text-navy">Q: </span>
              {e.question}
            </p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-foreground/80">
              <span className="font-semibold text-navy">A: </span>
              {e.answer}
            </p>
          </li>
        ))}
        {(entries.data ?? []).length === 0 && (
          <li className="rounded-xl border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground">
            No knowledge base entries yet. Promote real client questions from
            the Review tab to build it up.
          </li>
        )}
      </ul>
    </div>
  );
}
